import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/** Dark mode is a class on <html>, matching how the Django admin toggles it. */
const THEME_KEY = 'wer-admin-theme';
const ACCENT_KEY = 'wer-admin-accent';

export type Theme = 'light' | 'dark';

/** Accent palettes; each swaps the primary hue via html[data-theme]. */
export const ACCENTS = ['teal', 'indigo', 'amber', 'rose'] as const;
export type Accent = (typeof ACCENTS)[number];

/** Hues behind each accent's swatch dot; tokens.css holds the real palette. */
export const ACCENT_HUES: Record<Accent, number> = {
  teal: 184,
  indigo: 264,
  amber: 80,
  rose: 25,
};

export function readStoredTheme(): Theme {
  // The product default is dark; only an explicit 'light' choice overrides it.
  return localStorage.getItem(THEME_KEY) === 'light' ? 'light' : 'dark';
}

export function applyTheme(theme: Theme): void {
  document.documentElement.classList.toggle('dark', theme === 'dark');
  localStorage.setItem(THEME_KEY, theme);
}

export function readStoredAccent(): Accent {
  const stored = localStorage.getItem(ACCENT_KEY);
  return (ACCENTS as readonly string[]).includes(stored ?? '') ? (stored as Accent) : 'teal';
}

export function applyAccent(accent: Accent): void {
  document.documentElement.setAttribute('data-theme', accent);
  localStorage.setItem(ACCENT_KEY, accent);
}

/** How chart series are filled: solid colors, bklit line patterns, or gradients. */
const CHART_STYLE_KEY = 'wer-admin-chart-style';

export const CHART_STYLES = ['regular', 'pattern', 'gradient'] as const;
export type ChartStyle = (typeof CHART_STYLES)[number];

export function readStoredChartStyle(): ChartStyle {
  const stored = localStorage.getItem(CHART_STYLE_KEY);
  return (CHART_STYLES as readonly string[]).includes(stored ?? '')
    ? (stored as ChartStyle)
    : 'pattern';
}

export function storeChartStyle(style: ChartStyle): void {
  localStorage.setItem(CHART_STYLE_KEY, style);
}

/**
 * Seconds as a compact duration, matching _format_short_duration on the server
 * (e.g. 45s, 2m 05s, 1h 04m).
 */
export function formatShortDuration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || Number.isNaN(seconds)) return '—';
  const total = Math.max(0, Math.floor(seconds));
  if (total < 60) return `${total}s`;
  const minutes = Math.floor(total / 60);
  if (minutes < 60) return `${minutes}m ${String(total % 60).padStart(2, '0')}s`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ${String(minutes % 60).padStart(2, '0')}m`;
}

/** Seconds as mm:ss / h:mm:ss, for the audio player scrubber. */
export function formatTimecode(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || Number.isNaN(seconds)) return '0:00';
  const total = Math.max(0, Math.floor(seconds));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const secs = total % 60;
  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  }
  return `${minutes}:${String(secs).padStart(2, '0')}`;
}

/** "3 minutes ago" for realtime timestamps. */
export function formatRelative(iso: string | null | undefined): string {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const deltaSeconds = Math.round((Date.now() - then) / 1000);
  if (deltaSeconds < 45) return 'just now';
  if (deltaSeconds < 90) return 'a minute ago';
  const minutes = Math.round(deltaSeconds / 60);
  if (minutes < 60) return `${minutes} minutes ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.round(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
}

/** Debounce for search inputs, so typing does not fire a request per keystroke. */
export function debounce<Args extends unknown[]>(
  fn: (...args: Args) => void,
  waitMs: number,
): (...args: Args) => void {
  let timer: ReturnType<typeof setTimeout> | undefined;
  return (...args: Args) => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => fn(...args), waitMs);
  };
}

/** Stable, human-readable list joining: "a, b and c". */
export function joinWords(items: string[]): string {
  const parts = items.filter(Boolean);
  if (parts.length <= 1) return parts[0] ?? '';
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
}
